import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Search, Globe, Mail, Phone, MapPin, Sparkles, RefreshCw, Tag, Archive,
    Trash2, ExternalLink, ArrowRight, X, Lock, Filter, Building2, CheckCircle2,
    AlertCircle, Copy, ArchiveRestore, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import api, { backendUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DISCOVERY } from "@/constants/testIds/crm";
import HighlightText from "@/components/data-management/HighlightText";
import SavedViewsBar from "@/components/data-management/SavedViewsBar";
import { getStatusLabel, getStatusToneClasses } from "@/lib/statusStyles";

const inputCls = "w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors";

export default function Discovery() {
    const { isAdmin } = useAuth();
    const [facets, setFacets] = useState({ industries: [], providers: ["osm", "ddg", "sample"] });
    // Cascading location selection: {id, name} objects
    const [country, setCountry] = useState(null);
    const [stateSel, setStateSel] = useState(null);
    const [citySel, setCitySel] = useState(null);
    // Location option lists loaded from /api/locations/*
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [statesLoading, setStatesLoading] = useState(false);
    const [citiesLoading, setCitiesLoading] = useState(false);
    const [locationsReady, setLocationsReady] = useState(false);

    const [industry, setIndustry] = useState("");
    const [provider, setProvider] = useState("osm");
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [detail, setDetail] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [filter, setFilter] = useState({ q: "", validation: "" });
    const [limit, setLimit] = useState(25);
    const [progress, setProgress] = useState(null); // {stage, index, total, company, ...}
    const [summary, setSummary] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());

    const loadFacets = useCallback(async () => {
        try {
            const { data } = await api.get("/discovery/facets");
            setFacets(data);
        } catch { /* noop */ }
    }, []);

    // Load countries once on mount + poll status until locations seed completes.
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const [statusRes, countriesRes] = await Promise.all([
                    api.get("/locations/status"),
                    api.get("/locations/countries?limit=300"),
                ]);
                if (cancelled) return;
                setLocationsReady(statusRes.data.ready);
                setCountries(countriesRes.data.items || []);
                if (!statusRes.data.ready) {
                    // Poll again in 4s
                    setTimeout(load, 4000);
                }
            } catch { /* noop */ }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    // Cascade: when country changes, load its states and clear state/city.
    useEffect(() => {
        setStateSel(null);
        setCitySel(null);
        setStates([]);
        setCities([]);
        if (!country) return;
        setStatesLoading(true);
        api.get(`/locations/states?country_id=${country.id}&limit=500`)
            .then((r) => setStates(r.data.items || []))
            .catch(() => setStates([]))
            .finally(() => setStatesLoading(false));
    }, [country]);

    // Cascade: when state changes, load its cities and clear city.
    useEffect(() => {
        setCitySel(null);
        setCities([]);
        if (!stateSel) return;
        setCitiesLoading(true);
        api.get(`/locations/cities?state_id=${stateSel.id}&limit=1000`)
            .then((r) => setCities(r.data.items || []))
            .catch(() => setCities([]))
            .finally(() => setCitiesLoading(false));
    }, [stateSel]);

    const loadCompanies = useCallback(async () => {
        const params = new URLSearchParams();
        if (showArchived) params.set("include_archived", "true");
        if (filter.q) params.set("q", filter.q);
        if (filter.validation) params.set("validation_status", filter.validation);
        const { data } = await api.get(`/discovery/companies?${params}`);
        setItems(data.items);
    }, [showArchived, filter.q, filter.validation]);

    useEffect(() => { loadFacets(); loadCompanies(); }, [loadFacets, loadCompanies]);

    const search = async (e) => {
        e?.preventDefault();
        if (!country) { toast.error("Country is required"); return; }
        setBusy(true);
        setProgress({ stage: "searching", message: "Searching companies..." });
        setSummary(null);
        const payload = {
            industry: industry || null,
            country: country.name,
            state: stateSel?.name || null,
            city: citySel?.name || null,
            provider,
            limit,
        };
        try {
            const url = backendUrl("/api/discovery/search/stream");
            const resp = await fetch(url, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!resp.ok || !resp.body) throw new Error("Stream failed");
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop() || "";
                for (const p of parts) {
                    if (!p.startsWith("data:")) continue;
                    const json = p.slice(5).trim();
                    try {
                        const evt = JSON.parse(json);
                        if (evt.stage === "done") {
                            setSummary(evt.summary);
                            setProgress(null);
                            toast.success(`Found ${evt.summary.companies_found} companies · ${evt.summary.emails_found} emails`);
                            loadCompanies();
                        } else {
                            setProgress(evt);
                        }
                    } catch { /* ignore malformed */ }
                }
            }
        } catch (err) {
            toast.error(err.message || "Search failed");
            setProgress(null);
        } finally { setBusy(false); }
    };

    const toggleSelect = (id) => {
        setSelectedIds((s) => {
            const n = new Set(s);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    };
    const selectAllVisible = () => {
        setSelectedIds(new Set(items.map((i) => i.id)));
    };
    const clearSelection = () => setSelectedIds(new Set());
    const viewState = {
        industry,
        provider,
        country: country?.id || "",
        stateSel: stateSel?.id || "",
        citySel: citySel?.id || "",
        showArchived,
        filter,
        limit,
    };
    const applyState = (next) => {
        setIndustry(next.industry || "");
        setProvider(next.provider || "osm");
        setShowArchived(!!next.showArchived);
        setFilter(next.filter || { q: "", validation: "" });
        setLimit(Number(next.limit || 25));
    };

    const exportCsv = async (selectedOnly = false) => {
        const params = new URLSearchParams();
        if (selectedOnly && selectedIds.size > 0) {
            params.set("ids", [...selectedIds].join(","));
        }
        const url = backendUrl(`/api/discovery/export.csv?${params}`);
        const resp = await fetch(url, { credentials: "include" });
        const blob = await resp.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `lead-discovery-${Date.now()}.csv`;
        a.click();
        toast.success("CSV downloaded");
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid={DISCOVERY.root}>
            <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Prospecting</div>
                <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Lead Discovery</h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    Search businesses by industry &amp; location, then let Zyberzing crawl, validate, and score each site with AI.
                </p>
            </div>

            <SavedViewsBar
                storageKey="crm.discovery.views"
                state={viewState}
                applyState={applyState}
                defaultState={{ industry: "", provider: "osm", country: "", stateSel: "", citySel: "", showArchived: false, filter: { q: "", validation: "" }, limit: 25 }}
            />

            {/* Search form */}
            <form
                onSubmit={search}
                className="border border-border rounded-md bg-card p-5"
                data-testid={DISCOVERY.searchForm}
            >
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="space-y-1.5">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Industry</div>
                        <input
                            list="disc-industry-list"
                            value={industry}
                            onChange={(e) => setIndustry(e.target.value)}
                            placeholder="SaaS, Fintech, Consulting…"
                            data-testid={DISCOVERY.searchIndustry}
                            className={inputCls}
                        />
                        <datalist id="disc-industry-list">
                            {facets.industries.map((o) => <option key={o} value={o} />)}
                        </datalist>
                    </div>
                    <SearchableSelect
                        label={<>Country <span className="text-destructive">*</span></>}
                        placeholder={locationsReady ? "Select country…" : "Loading worldwide DB…"}
                        options={countries}
                        value={country}
                        onChange={setCountry}
                        disabled={countries.length === 0}
                        testId={DISCOVERY.searchCountry}
                    />
                    <SearchableSelect
                        label="State / Province"
                        placeholder={country ? (statesLoading ? "Loading…" : "Select state…") : "Pick a country first"}
                        options={states}
                        value={stateSel}
                        onChange={setStateSel}
                        disabled={!country || statesLoading}
                        testId={DISCOVERY.searchState}
                        emptyText="No states/provinces for this country."
                    />
                    <SearchableSelect
                        label="City"
                        placeholder={stateSel ? (citiesLoading ? "Loading…" : "Select city…") : "Pick a state first"}
                        options={cities}
                        value={citySel}
                        onChange={setCitySel}
                        disabled={!stateSel || citiesLoading}
                        testId={DISCOVERY.searchCity}
                        emptyText="No cities found for this state."
                    />
                    <div className="space-y-1.5">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Provider</div>
                        <select className={inputCls} value={provider} onChange={(e) => setProvider(e.target.value)}>
                            {facets.providers.map((p) => (
                                <option key={p} value={p}>
                                    {p === "osm" ? "OpenStreetMap (free, location-first)" :
                                     p === "ddg" ? "DuckDuckGo web (free, niche-friendly)" :
                                     p === "sample" ? "Sample (offline demo)" : p}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="space-y-1.5">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Result count</div>
                        <select data-testid="discovery-search-limit" className={inputCls} value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                            {[10, 25, 50, 100, 250, 500].map((n) => <option key={n} value={n}>{n} companies</option>)}
                        </select>
                    </div>
                </div>
                <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
                    <div className="text-xs text-muted-foreground max-w-2xl">
                        <span className="font-semibold text-foreground">Worldwide geo database:</span>{" "}
                        {locationsReady
                            ? <>All ISO countries · 5,000+ states · 150,000+ cities · cascaded via <code className="text-primary font-mono">/api/locations/*</code>.</>
                            : <>Downloading location database in the background (~35 MB, one-time)…</>}
                    </div>
                    <button
                        type="submit"
                        disabled={busy || !country}
                        data-testid={DISCOVERY.searchButton}
                        className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 active:scale-[0.98] transition-colors transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        <Search className="h-4 w-4" />
                        {busy ? "Searching & crawling…" : "Search"}
                    </button>
                </div>
            </form>

            {/* Progress panel (live streaming) */}
            {progress && (
                <div className="border border-primary/40 rounded-md bg-card p-5 space-y-3 animate-fade-in" data-testid="discovery-progress-panel">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <RefreshCw className="h-4 w-4 text-primary animate-spin" />
                            <div>
                                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">Live progress</div>
                                <div className="text-sm font-medium mt-0.5">
                                    {progress.stage === "searching" && "Searching companies…"}
                                    {progress.stage === "found" && `Found ${progress.count} candidates${progress.fallback_used ? ` (+ ${progress.fallback_used.toUpperCase()} fallback)` : ""}`}
                                    {progress.stage === "crawling" && `Crawling ${progress.company}…`}
                                    {progress.stage === "company_done" && `Extracted ${progress.emails} emails · ${progress.phones} phones — validating…`}
                                    {progress.stage === "company_error" && `Error on last site — continuing`}
                                </div>
                            </div>
                        </div>
                        {progress.index && progress.total && (
                            <div className="font-mono text-xs text-muted-foreground">{progress.index} / {progress.total}</div>
                        )}
                    </div>
                    {progress.total > 0 && (
                        <div className="h-2 rounded-full bg-secondary overflow-hidden">
                            <div className="h-full bg-primary transition-all" style={{ width: `${(progress.index / progress.total) * 100}%` }} />
                        </div>
                    )}
                    <div className="flex flex-wrap gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                        {["Searching", "Crawling", "Extracting emails", "Extracting phones", "Validating emails", "Saving results"].map((step, i) => {
                            const active = (
                                (progress.stage === "searching" && i === 0) ||
                                (progress.stage === "found" && i === 0) ||
                                (progress.stage === "crawling" && i === 1) ||
                                (progress.stage === "company_done" && i >= 2 && i <= 4) ||
                                (progress.stage === "done" && i === 5)
                            );
                            return (
                                <span key={step} className={`px-2 py-0.5 rounded border ${active ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>
                                    {step}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Summary card after completion */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 animate-fade-in" data-testid="discovery-summary">
                    {[
                        { k: "companies_found", label: "Companies", tone: "bg-primary/10 text-primary" },
                        { k: "websites_crawled", label: "Crawled", tone: "bg-success/15 text-success" },
                        { k: "emails_found", label: "Emails", tone: "bg-success/15 text-success" },
                        { k: "phones_found", label: "Phones", tone: "bg-primary/10 text-primary" },
                        { k: "failed_websites", label: "Failed", tone: "bg-destructive/15 text-destructive" },
                        { k: "duplicates_skipped", label: "Duplicates skipped", tone: "bg-warning/15 text-warning" },
                    ].map((s) => (
                        <div key={s.k} className="border border-border rounded-md bg-card p-4">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{s.label}</div>
                            <div className={`font-heading text-2xl font-bold tabular-nums mt-1 inline-block px-2 rounded ${s.tone}`}>
                                {summary[s.k] ?? 0}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Meta pills — cache-hit + fallback surfaces */}
            {summary && (
                <div className="flex items-center gap-2 flex-wrap animate-fade-in" data-testid="discovery-meta-pills">
                    {summary.geocode_cache_hit === true && (
                        <span
                            data-testid="discovery-cache-hit-pill"
                            title="Nominatim geocode served from local cache — no external request made"
                            className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-success/10 text-success border border-success/30 text-[10px] font-semibold uppercase tracking-wider"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
                            Cache hit
                        </span>
                    )}
                    {summary.geocode_cache_hit === false && (
                        <span
                            title="Nominatim geocode was fetched fresh and cached for next time"
                            className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-secondary text-muted-foreground text-[10px] font-semibold uppercase tracking-wider"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60" />
                            Fresh geocode
                        </span>
                    )}
                    {summary.fallback_used && (
                        <span
                            data-testid="discovery-fallback-pill"
                            title={`OSM returned thin data — auto-fell back to ${summary.fallback_used.toUpperCase()} to expand coverage`}
                            className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-warning/10 text-warning border border-warning/30 text-[10px] font-semibold uppercase tracking-wider"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-warning" />
                            Fallback → {summary.fallback_used}
                        </span>
                    )}
                </div>
            )}

            {/* Filter row */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                        value={filter.q}
                        onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                        placeholder="Filter by name, website, summary…"
                        data-testid={DISCOVERY.listFilterInput}
                        className="w-full h-9 pl-9 pr-3 rounded-md border border-border bg-card text-sm"
                    />
                </div>
                <select
                    value={filter.validation}
                    onChange={(e) => setFilter({ ...filter, validation: e.target.value })}
                    data-testid={DISCOVERY.listFilterValidation}
                    className="h-9 px-2 rounded-md border border-border bg-card text-xs"
                >
                    <option value="">All validation</option>
                    <option value="valid">Valid</option>
                    <option value="risky">Risky</option>
                    <option value="invalid">Invalid</option>
                    <option value="unknown">Unknown</option>
                </select>
                <button
                    onClick={() => setShowArchived((v) => !v)}
                    data-testid={DISCOVERY.toggleArchived}
                    className={`h-9 px-3 rounded-md border text-xs uppercase tracking-wider transition-colors ${
                        showArchived ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
                    }`}
                >
                    {showArchived ? "Hiding archived" : "Show archived"}
                </button>
                <button
                    onClick={loadCompanies}
                    data-testid={DISCOVERY.refreshList}
                    className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border hover:bg-secondary transition-colors"
                    title="Refresh"
                >
                    <RefreshCw className="h-4 w-4" />
                </button>
                {/* Bulk actions bar */}
                <div className="flex items-center gap-2 ml-auto">
                    {selectedIds.size > 0 && (
                        <>
                            <span className="text-xs text-muted-foreground">{selectedIds.size} selected</span>
                            <button onClick={clearSelection} className="h-9 px-3 rounded-md border border-border hover:bg-secondary text-xs">Clear</button>
                            <button onClick={() => exportCsv(true)} data-testid="discovery-export-selected" className="h-9 px-3 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 inline-flex items-center gap-1.5">
                                Export selected CSV
                            </button>
                        </>
                    )}
                    <button onClick={selectAllVisible} className="h-9 px-3 rounded-md border border-border hover:bg-secondary text-xs">Select all visible</button>
                    <button onClick={() => exportCsv(false)} data-testid="discovery-export-all" className="h-9 px-3 rounded-md border border-primary/40 text-primary text-xs font-medium hover:bg-primary/10 transition-colors">
                        Export all CSV
                    </button>
                </div>
            </div>

            {/* Results grid */}
            {items.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-md bg-card/40 text-muted-foreground">
                    <Globe className="h-8 w-8 mx-auto mb-3 opacity-40" />
                    <div className="text-sm">No discovered companies yet. Run a search above to populate.</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {items.map((c, i) => (
                        <motion.div
                            key={c.id}
                            data-testid={DISCOVERY.card}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.03 }}
                            className={`relative text-left border rounded-md p-5 hover:-translate-y-[1px] transition-colors transition-transform group ${getStatusToneClasses(c.status, { group: "status", variant: "surface" })}`}
                        >
                            <div className="absolute top-3 left-3">
                                <input
                                    type="checkbox"
                                    checked={selectedIds.has(c.id)}
                                    onChange={() => toggleSelect(c.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid="discovery-card-checkbox"
                                    className="h-4 w-4 rounded border-border accent-primary cursor-pointer"
                                />
                            </div>
                            <button onClick={() => setDetail(c)} className="w-full text-left">
                                <div className="flex items-start justify-between gap-2 pl-6">
                                    <div className="min-w-0">
                                        <div className="font-heading text-lg font-semibold tracking-tight truncate"><HighlightText text={c.company_name} query={filter.q} /></div>
                                        <div className="text-[11px] text-muted-foreground truncate">{c.website?.replace(/^https?:\/\//, "")}</div>
                                    </div>
                                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${getStatusToneClasses(c.status, { group: "status", variant: "badge" })}`}>
                                        {getStatusLabel(c.status)}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                    {c.industry && <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded px-1.5 py-0.5">{c.industry}</span>}
                                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                                        <MapPin className="h-2.5 w-2.5" /> {[c.city, c.state, c.country].filter(Boolean).join(", ")}
                                    </span>
                                </div>
                                <div className="mt-3 space-y-1 text-xs">
                                    {c.emails?.[0] && (
                                        <div className="flex items-center gap-1.5 text-muted-foreground">
                                            <Mail className="h-3 w-3 shrink-0" />
                                            <span className="font-mono truncate"><HighlightText text={c.emails[0]} query={filter.q} /></span>
                                            {c.emails.length > 1 && <span className="text-[10px]">+{c.emails.length - 1}</span>}
                                        </div>
                                    )}
                                    {c.phones?.[0] && (
                                        <div className="flex items-center gap-1.5 text-muted-foreground">
                                            <Phone className="h-3 w-3 shrink-0" />
                                            <span className="font-mono">{c.phones[0]}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-3 flex items-center justify-between">
                                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${getStatusToneClasses(c.validation_status, { group: "validation", variant: "badge" })}`}>
                                        {getStatusLabel(c.validation_status)}
                                    </span>
                                    <span className="text-[10px] font-mono text-muted-foreground">{c.website_status || "—"}</span>
                                </div>
                            </button>
                        </motion.div>
                    ))}
                </div>
            )}

            <AnimatePresence>
                {detail && (
                    <DiscoveryDetail
                        company={detail}
                        onClose={() => setDetail(null)}
                        onChange={(updated) => { setDetail(updated); loadCompanies(); }}
                        isAdmin={isAdmin}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

function FacetInput({ label, value, onChange, options, placeholder, testId }) {
    return (
        <div className="space-y-1.5">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
            <input
                list={`facet-${label}`}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testId}
                className={inputCls}
            />
            <datalist id={`facet-${label}`}>
                {options.map((o) => <option key={o} value={o} />)}
            </datalist>
        </div>
    );
}

function SearchableSelect({ label, placeholder, options, value, onChange, disabled, testId, emptyText }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const boxRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        function onDoc(e) { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 20);
    }, [open]);

    const filtered = q
        ? options.filter((o) => o.name.toLowerCase().includes(q.toLowerCase())).slice(0, 200)
        : options.slice(0, 200);

    return (
        <div className="space-y-1.5 relative" ref={boxRef}>
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
            <button
                type="button"
                data-testid={testId}
                disabled={disabled}
                onClick={() => !disabled && setOpen((v) => !v)}
                className={`w-full h-10 px-3 rounded-md border border-input bg-background text-sm text-left flex items-center justify-between gap-2 transition-colors ${
                    disabled ? "opacity-50 cursor-not-allowed" : "hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/40"
                }`}
            >
                <span className={`truncate ${value ? "" : "text-muted-foreground"}`}>
                    {value?.name || placeholder}
                </span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute z-40 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-72 overflow-hidden flex flex-col animate-fade-in">
                    <div className="p-2 border-b border-border">
                        <input
                            ref={inputRef}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Type to search…"
                            data-testid={`${testId}-search-input`}
                            className="w-full h-8 px-2 rounded border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                    </div>
                    <div className="overflow-y-auto flex-1">
                        {filtered.length === 0 ? (
                            <div className="px-3 py-6 text-sm text-muted-foreground text-center" data-testid={`${testId}-empty`}>
                                {options.length === 0 ? (emptyText || "No results found.") : "No matches for that search."}
                            </div>
                        ) : (
                            filtered.map((o) => (
                                <button
                                    key={o.id}
                                    type="button"
                                    onClick={() => { onChange(o); setOpen(false); setQ(""); }}
                                    data-testid={`${testId}-option`}
                                    className={`w-full text-left px-3 py-1.5 hover:bg-secondary text-sm transition-colors ${
                                        value?.id === o.id ? "bg-primary/10 text-primary font-medium" : ""
                                    }`}
                                >
                                    <span className="truncate block">{o.name}</span>
                                    {o.iso2 && <span className="text-[10px] text-muted-foreground font-mono ml-1">{o.iso2}</span>}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function DiscoveryDetail({ company, onClose, onChange, isAdmin }) {
    const [c, setC] = useState(company);
    const [busy, setBusy] = useState({});
    const [tagInput, setTagInput] = useState("");

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const setBusyKey = (k, v) => setBusy((b) => ({ ...b, [k]: v }));

    const recrawl = async () => {
        setBusyKey("crawl", true);
        try {
            const { data } = await api.post(`/discovery/companies/${c.id}/crawl`);
            setC(data); onChange && onChange(data);
            toast.success("Re-crawled");
        } catch { toast.error("Crawl failed"); }
        setBusyKey("crawl", false);
    };
    const enrich = async () => {
        setBusyKey("ai", true);
        try {
            const { data } = await api.post(`/discovery/companies/${c.id}/enrich-ai`);
            setC(data); onChange && onChange(data);
            toast.success(`AI enriched (${data.ai_source || "ok"})`);
        } catch { toast.error("AI enrichment failed"); }
        setBusyKey("ai", false);
    };
    const convert = async () => {
        setBusyKey("convert", true);
        try {
            await api.post(`/discovery/companies/${c.id}/convert`);
            toast.success("Converted to CRM lead");
            onChange && onChange({ ...c, tags: [...(c.tags || []), "converted"] });
        } catch { toast.error("Convert failed"); }
        setBusyKey("convert", false);
    };
    const addTag = async () => {
        if (!tagInput.trim()) return;
        const tags = [...(c.tags || []), tagInput.trim()];
        const { data } = await api.post(`/discovery/companies/${c.id}/tags`, { tags });
        setC(data); onChange && onChange(data); setTagInput("");
    };
    const removeTag = async (t) => {
        const tags = (c.tags || []).filter((x) => x !== t);
        const { data } = await api.post(`/discovery/companies/${c.id}/tags`, { tags });
        setC(data); onChange && onChange(data);
    };
    const archive = async () => {
        const endpoint = c.status === "archived" ? "unarchive" : "archive";
        await api.post(`/discovery/companies/${c.id}/${endpoint}`);
        const next = { ...c, status: endpoint === "archive" ? "archived" : "crawled" };
        setC(next); onChange && onChange(next);
        toast.success(endpoint === "archive" ? "Archived" : "Restored");
    };
    const remove = async () => {
        if (!window.confirm(`Delete ${c.company_name} from discovery database?`)) return;
        try {
            await api.delete(`/discovery/companies/${c.id}`);
            toast.success("Deleted");
            onChange && onChange(null);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    };
    const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm"
            onClick={onClose}
        >
            <motion.aside
                initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                data-testid={DISCOVERY.detailDrawer}
                className="ml-auto h-full w-full max-w-2xl bg-card border-l border-border overflow-y-auto"
            >
                {/* Header */}
                <div className="p-6 border-b border-border sticky top-0 bg-card z-10">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${getStatusToneClasses(c.status, { group: "status", variant: "badge" })}`}>{getStatusLabel(c.status)}</span>
                                <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${getStatusToneClasses(c.validation_status, { group: "validation", variant: "badge" })}`}>{getStatusLabel(c.validation_status)}</span>
                            </div>
                            <h3 className="font-heading text-2xl font-bold tracking-tight mt-2 truncate">{c.company_name}</h3>
                            {c.website && (
                                <a href={c.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1">
                                    {c.website} <ExternalLink className="h-3 w-3" />
                                </a>
                            )}
                            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                                <span>{c.industry}</span>
                                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {[c.city, c.state, c.country].filter(Boolean).join(", ")}</span>
                                <span>Website: <span className="font-mono">{c.website_status || "n/a"}</span></span>
                                {c.crawl_date && <span>Crawled {new Date(c.crawl_date).toLocaleDateString()}</span>}
                            </div>
                        </div>
                        <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-secondary shrink-0">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-5 flex-wrap">
                        <button onClick={convert} disabled={busy.convert} data-testid={DISCOVERY.actionConvert}
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors">
                            <ArrowRight className="h-3.5 w-3.5" /> Convert to Lead
                        </button>
                        <a href={c.emails?.[0] ? `mailto:${c.emails[0]}?subject=${encodeURIComponent("Hello from Zyberzing")}&body=${encodeURIComponent(c.ai_cold_email || "")}` : "#"}
                            data-testid={DISCOVERY.actionEmail}
                            aria-disabled={!c.emails?.length}
                            title={c.emails?.length ? `Email ${c.emails[0]}` : "No validated email available — run Re-crawl or add tags"}
                            onClick={(e) => { if (!c.emails?.length) { e.preventDefault(); toast.error("No email to send to"); } }}
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium transition-colors ${
                                c.emails?.length ? "bg-secondary hover:bg-secondary/80" : "bg-secondary/40 text-muted-foreground cursor-not-allowed"
                            }`}>
                            <Mail className="h-3.5 w-3.5" /> Send Email
                        </a>
                        <button onClick={enrich} disabled={busy.ai} data-testid={DISCOVERY.actionEnrichAi}
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-primary/40 text-primary text-xs font-medium hover:bg-primary/10 disabled:opacity-60 transition-colors">
                            <Sparkles className="h-3.5 w-3.5" /> {busy.ai ? "Enriching…" : "AI Enrich"}
                        </button>
                        <button onClick={recrawl} disabled={busy.crawl} data-testid={DISCOVERY.actionRecrawl}
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-border text-xs hover:bg-secondary disabled:opacity-60 transition-colors">
                            <RefreshCw className={`h-3.5 w-3.5 ${busy.crawl ? "animate-spin" : ""}`} /> Re-crawl
                        </button>
                        <button onClick={archive} data-testid={DISCOVERY.actionArchive}
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-border text-xs hover:bg-secondary transition-colors">
                            {c.status === "archived" ? <><ArchiveRestore className="h-3.5 w-3.5" /> Restore</> : <><Archive className="h-3.5 w-3.5" /> Archive</>}
                        </button>
                        {isAdmin ? (
                            <button onClick={remove} data-testid={DISCOVERY.actionDelete}
                                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-destructive/40 text-destructive text-xs font-medium hover:bg-destructive/10 transition-colors">
                                <Trash2 className="h-3.5 w-3.5" /> Delete
                            </button>
                        ) : (
                            <span title="Admin only" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-border text-muted-foreground/60 text-xs cursor-not-allowed">
                                <Lock className="h-3.5 w-3.5" /> Delete
                            </span>
                        )}
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6">
                    {/* Contact info */}
                    <section>
                        <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">Contact</h4>
                        <div className="border border-border rounded-md divide-y divide-border">
                            {c.emails?.length > 0 ? c.emails.map((e) => (
                                <div key={e} className="flex items-center gap-2 px-3 py-2 text-sm">
                                    <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                                    <span className="flex-1 font-mono text-xs truncate">{e}</span>
                                    <button onClick={() => copy(e)} className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-secondary"><Copy className="h-3 w-3" /></button>
                                    <a href={`mailto:${e}`} className="h-6 px-2 inline-flex items-center rounded text-[11px] text-primary hover:bg-primary/10">Mail</a>
                                </div>
                            )) : <div className="text-xs text-muted-foreground px-3 py-3">No emails discovered.</div>}
                            {c.phones?.map((p) => (
                                <div key={p} className="flex items-center gap-2 px-3 py-2 text-sm">
                                    <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                                    <span className="flex-1 font-mono text-xs">{p}</span>
                                    <button onClick={() => copy(p)} className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-secondary"><Copy className="h-3 w-3" /></button>
                                </div>
                            ))}
                            {c.contact_page && (
                                <div className="flex items-center gap-2 px-3 py-2 text-sm">
                                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                                    <a href={c.contact_page} target="_blank" rel="noreferrer" className="text-primary text-xs hover:underline truncate">{c.contact_page}</a>
                                </div>
                            )}
                            {c.address && (
                                <div className="flex items-start gap-2 px-3 py-2 text-sm">
                                    <MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
                                    <span className="text-xs text-muted-foreground">{c.address}</span>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Socials */}
                    {c.socials && Object.keys(c.socials).length > 0 && (
                        <section>
                            <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">Social</h4>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(c.socials).map(([plat, url]) => (
                                    <a key={plat} href={url} target="_blank" rel="noreferrer"
                                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-border hover:bg-secondary text-xs transition-colors">
                                        <span className="capitalize">{plat}</span>
                                        <ExternalLink className="h-3 w-3" />
                                    </a>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* AI Summary */}
                    <section>
                        <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-1">
                            <Sparkles className="h-3 w-3 text-primary" /> AI Summary
                        </h4>
                        {c.ai_summary ? (
                            <div className="space-y-3">
                                <div className="border border-border rounded-md p-3 text-sm">
                                    {c.ai_summary}
                                    {c.ai_category && <div className="mt-2 text-[11px] uppercase tracking-wider text-muted-foreground">Category: <span className="text-primary font-medium">{c.ai_category}</span></div>}
                                </div>
                                {c.ai_service_matches?.length > 0 && (
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Zyberzing service fit</div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {c.ai_service_matches.map((s) => (
                                                <span key={s} className="text-[11px] px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/30">{s}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {c.ai_cold_email && (
                                    <div>
                                        <div className="flex items-center justify-between mb-1.5">
                                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Cold email draft</div>
                                            <button onClick={() => copy(c.ai_cold_email)} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1">
                                                <Copy className="h-3 w-3" /> Copy
                                            </button>
                                        </div>
                                        <pre className="whitespace-pre-wrap text-xs bg-secondary/40 border border-border rounded-md p-3 font-sans leading-relaxed">{c.ai_cold_email}</pre>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="border border-dashed border-border rounded-md p-4 text-center">
                                <div className="text-sm text-muted-foreground mb-3">No AI enrichment yet.</div>
                                <button onClick={enrich} disabled={busy.ai} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors">
                                    <Sparkles className="h-3.5 w-3.5" /> {busy.ai ? "Enriching…" : "Run AI enrichment"}
                                </button>
                            </div>
                        )}
                    </section>

                    {/* Tags */}
                    <section>
                        <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">Tags</h4>
                        <div className="flex flex-wrap gap-2 mb-2">
                            {(c.tags || []).map((t) => (
                                <span key={t} className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-primary/10 text-primary text-xs">
                                    {t}
                                    <button onClick={() => removeTag(t)} className="hover:text-destructive"><X className="h-3 w-3" /></button>
                                </span>
                            ))}
                            {(c.tags || []).length === 0 && <span className="text-xs text-muted-foreground">No tags yet.</span>}
                        </div>
                        <div className="flex gap-2">
                            <input
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                                placeholder="Add tag (press Enter)"
                                data-testid={DISCOVERY.tagInput}
                                className="flex-1 h-9 px-3 rounded-md border border-input bg-background text-xs focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                            <button onClick={addTag} data-testid={DISCOVERY.tagAdd} className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-xs hover:bg-primary/90 transition-colors">Add</button>
                        </div>
                    </section>
                </div>
            </motion.aside>
        </motion.div>
    );
}
